import { defaultTo, get } from 'lodash';
import { ICommissionBreakdownObject } from '../../../../data/entities/commission-breakdown';
import { IBookingBreakdown, IBookingExtraItems, IBookingV2 } from '../../../../data/entities/event/booking';
import { IAppliedPromotion } from '../../../../data/entities/promotions';
import { processCommissionBreakdown } from '../helpers';
import { IFinanceCommissionBreakdownRow } from './FinanceBreakdown';

const CLEANING_FEE_RESTRICTION = 'cleaning fee';

export const percentOfTbv = (tbv: number, value: number) => Math.abs(value / tbv) * 100;

export const calculateVat = (vatRate: number, value: number) => Math.abs(value) * vatRate;

export const getExtraFeesAmount = (extraFees: IBookingExtraItems[], blacklist: string[] = []) =>
	extraFees
		.filter((fee: IBookingExtraItems) => !blacklist.includes(fee.name))
		.map((fee: IBookingExtraItems) => fee.amount)
		.reduce((sum: number, amount: number) => sum + amount, 0);

export const getCleaningFee = (extraFees: IBookingExtraItems[]) => {
	const cleaningFee = extraFees.find((fee: IBookingExtraItems) => fee.name === CLEANING_FEE_RESTRICTION);

	return cleaningFee ? cleaningFee.amount : 0;
};

type FinanceCommissionNumericalKeys = Omit<IFinanceCommissionBreakdownRow, 'name' | 'info' | 'is_rate'>;

export const presentationValueForCommission = (row: IFinanceCommissionBreakdownRow, key: keyof FinanceCommissionNumericalKeys) => {
	if (row.is_rate) {
		return row[key] ?? '-';
	} else {
		return row[key]?.toFixed(2) ?? '-';
	}
};

const COMMISSION_2021_CHANGE_KEYS = [
	'presented_travelnest_rate',
	'presented_travelnest_value',
	'presented_travelnest_net_value',
	'presented_travelnest_vat_value',
	'presented_channel_rate',
	'presented_channel_value',
	'presented_channel_net_value',
	'presented_channel_vat_value',
	'total_adjustment_commission_rate',
	'total_adjustment_commission_value',
	'total_adjustment_commission_net_value',
	'total_adjustment_commission_vat_value',
] as const;

export const bookingHas2021CommissionValues = (breakdown: IBookingBreakdown): boolean => {
	const tnBreakdown = breakdown.travelnest ?? {};
	return COMMISSION_2021_CHANGE_KEYS.every((k) => tnBreakdown[k] !== null && tnBreakdown[k] !== undefined);
};

export const getBookingCommissionRows = (
	booking: IBookingV2,
	commissionBreakdown: ICommissionBreakdownObject,
	appliedPromos: IAppliedPromotion[]): IFinanceCommissionBreakdownRow[] => {

	const breakdown = booking.value ? booking.value.breakdown : {} as IBookingBreakdown;

	const promotionalCommissionAdjustment = processCommissionBreakdown(breakdown, commissionBreakdown, appliedPromos);

	const commissionAdjustmentRateSum = promotionalCommissionAdjustment.reduce((accumulator, curr) => {
		if (curr.rate && breakdown.travelnest?.total_adjustment_commission_rate !== 0) {
			return accumulator + curr.rate;
		}
		return 0;
	}, 0);

	const commissionAdjustmentValueSum = promotionalCommissionAdjustment.reduce((accumulator, curr) => {
		if (curr.val && breakdown.travelnest?.total_adjustment_commission_rate !== 0) {
			return accumulator + curr.val;
		}
		return 0;
	}, 0);

	const ownerRent = get(breakdown, 'owner.rent_value', 0) || 0;
	const discountedOwnerRent = get(breakdown, 'owner.discounted_owner_rent_value', ownerRent);
	const ownerExtras = get(breakdown, 'owner.extras.items', []) as IBookingExtraItems[];
	const extraFees = getExtraFeesAmount(ownerExtras, []);
	// vat_rate is always 0.2 so must figure out if booking breakdown was made with vat
	const currentVATAmount = defaultTo(get(breakdown, 'travelnest.commission_vat_value'), 0);
	const vatRate = currentVATAmount > 0 ? get(breakdown, 'travelnest.vat_rate') : 0;

	if (bookingHas2021CommissionValues(breakdown)) {
		const totalCommissionRate = defaultTo(get(breakdown, 'travelnest.commission_rate'), 0);
		const tnRate = defaultTo(get(breakdown, 'travelnest.presented_travelnest_rate'), 0);
		const channelRate = defaultTo(get(breakdown, 'travelnest.presented_channel_rate'), 0);
		const adjustmentRate = defaultTo(get(breakdown, 'travelnest.total_adjustment_commission_rate'), 0);

		// The original commission values the booking began with
		const originalTnValueNet = (discountedOwnerRent + extraFees) * tnRate;
		const originalChannelValueNet = (discountedOwnerRent + extraFees) * channelRate;
		const originalAdjustmentValueNet = (discountedOwnerRent + extraFees) * adjustmentRate;
		const originalVATAmount = (originalTnValueNet + originalChannelValueNet + originalAdjustmentValueNet) * vatRate;
		const originalValueGross = originalTnValueNet + originalChannelValueNet + originalAdjustmentValueNet + originalVATAmount;

		// The current values (after any post payments)
		const currentTnValueNet = defaultTo(get(breakdown, 'travelnest.presented_travelnest_net_value'), 0);
		const currentChannelValueNet = defaultTo(get(breakdown, 'travelnest.presented_channel_net_value'), 0);
		const currentAdjustmentValueNet = defaultTo(get(breakdown, 'travelnest.total_adjustment_commission_net_value'), 0);

		const currentValueGross = get(breakdown, 'travelnest.commission_value');

		// The difference between original and current (due to post payments)
		const amendedTnValueNet = currentTnValueNet - originalTnValueNet;
		const amendedChannelValueNet = currentChannelValueNet - originalChannelValueNet;
		const amendedAdjustmentValueNet = currentAdjustmentValueNet - originalAdjustmentValueNet;
		const amendedVATAmount = currentVATAmount - originalVATAmount;
		const amendedValueGross = currentValueGross - originalValueGross;

		return [
			{
				name: 'Percentage',
				is_rate: true,
				travelnest_fee: tnRate * 100,
				channel_fee: (channelRate * 100) + (commissionAdjustmentRateSum * 100),
				adjustment_fee: (adjustmentRate * 100) - (commissionAdjustmentRateSum * 100),
				vat: vatRate * 100,
				total_fee: totalCommissionRate * 100
			},
			{
				name: 'Original Fees',
				is_rate: false,
				travelnest_fee: originalTnValueNet,
				channel_fee: originalChannelValueNet + commissionAdjustmentValueSum,
				adjustment_fee: originalAdjustmentValueNet - commissionAdjustmentValueSum,
				vat: originalVATAmount,
				total_fee: originalValueGross
			},
			{
				name: 'Fee amendments',
				is_rate: false,
				travelnest_fee: amendedTnValueNet,
				channel_fee: amendedChannelValueNet,
				adjustment_fee: amendedAdjustmentValueNet,
				vat: amendedVATAmount,
				total_fee: amendedValueGross
			},
			{
				name: 'Effective Fees',
				is_rate: false,
				travelnest_fee: currentTnValueNet,
				channel_fee: currentChannelValueNet + commissionAdjustmentValueSum,
				adjustment_fee: currentAdjustmentValueNet - commissionAdjustmentValueSum,
				vat: currentVATAmount,
				total_fee: currentValueGross
			},
		];
	} else { // Pre-commission change bookings do not have commission broken down into TN/Channel/Adjustment
		const originalTravelNestCommissionNet = (discountedOwnerRent + extraFees) * get(breakdown, 'travelnest.commission_rate');
		const originalTravelNestCommissionVat = originalTravelNestCommissionNet * vatRate;
		const originalTravelNestCommissionGross = originalTravelNestCommissionNet + originalTravelNestCommissionVat;

		return [
			{
				name: 'Percentage',
				is_rate: true,
				travelnest_fee: null,
				channel_fee: null,
				adjustment_fee: null,
				vat: defaultTo(vatRate, 0) * 100,
				total_fee: defaultTo(get(breakdown, 'travelnest.commission_rate'), 0) * 100
			},
			{
				name: 'Original Fees',
				is_rate: false,
				travelnest_fee: null,
				channel_fee: null,
				adjustment_fee: null,
				vat: originalTravelNestCommissionVat,
				total_fee: originalTravelNestCommissionGross
			},
			{
				name: 'Fee amendments',
				is_rate: false,
				travelnest_fee: null,
				channel_fee: null,
				adjustment_fee: null,
				vat: defaultTo(get(breakdown, 'travelnest.commission_vat_value'), 0) - originalTravelNestCommissionVat,
				total_fee: defaultTo(get(breakdown, 'travelnest.commission_value'), 0) - originalTravelNestCommissionGross
			},
			{
				name: 'Effective Fees',
				is_rate: false,
				travelnest_fee: null,
				channel_fee: null,
				adjustment_fee: null,
				vat: defaultTo(get(breakdown, 'travelnest.commission_vat_value'), 0),
				total_fee: defaultTo(get(breakdown, 'travelnest.commission_value'), 0)
			},
		];
	}
};
